import React, { useEffect, useState, useRef } from "react";
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/UnsavedChangesDialog';
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
  Switch,
  FormHelperText,
  Divider,
  FormControlLabel,
} from "@mui/material";
import { toast } from "react-toastify";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PreviewIcon from "@mui/icons-material/Preview";
import InventoryIcon from "@mui/icons-material/Inventory";
import CalculateIcon from "@mui/icons-material/Calculate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import NotesIcon from "@mui/icons-material/Notes";
import PaymentIcon from "@mui/icons-material/Payment";
import DiscountIcon from "@mui/icons-material/Discount";
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from "react-i18next";
import html2pdf from 'html2pdf.js';

// Services
import * as deliveryNoteService from "../services/deliveryNoteService";
import { generateDeliveryNotePdfHtml } from "../services/deliveryNotePdfService";
import { searchProducts } from "../../product/services/productService";
import { searchCustomers } from "../../customers/services/customerService";

// Redux actions
import {
  setSelectedCustomer,
  setSelectedProduct,
  setQuantity,
  setPrice,
  setCustomText,
} from "../../../app/store/slices/deliveryNoteSlice";

// Components
import CustomerSelect from "../../invoices/components/CustomerSelect";
import ProductSelect from "../../invoices/components/ProductSelect";
import InvoicePreviewDialog from "../../../components/InvoicePreviewDialog";
import AddCustomerDialog from "../../customers/components/AddCustomerDialog";
import AddProductDialog from "../../product/components/AddProductDialog";

// Payment imports
import PaymentSection from "../../invoices/components/PaymentSection";
import MultiplePaymentMethodsSection from "../../invoices/components/MultiplePaymentMethodsSection";
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import * as vatService from '../../companysettings/services/vatService';
import * as documentNoteTemplateService from '../../companysettings/services/documentNoteTemplateService';
import { DocumentType, getDefaultTemplateId, getTemplateContent } from '../../../utils/documentNoteTemplateUtils';

// Hooks
import useStoreInfo from '../../../hooks/useCompanyInfo';

const MAX_CUSTOM_TEXT_LENGTH = 1000;

// Generate delivery note number as DDMMYY + 4 random digits
function generateDeliveryNoteNumber() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const rand = String(Math.floor(1000 + Math.random() * 9000));
  return `DN-${dd}${mm}${yy}${rand}`;
}

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function AddDeliveryNotePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: deliveryNoteId } = useParams();
  const isEditMode = Boolean(deliveryNoteId);
  
  const {
    selectedCustomer,
    selectedProduct,
    quantity,
    price,
    customText,
  } = useSelector((state) => state.deliveryNote);
  
  // Get store info from Redux store
  const { storeInfo } = useStoreInfo();
  
  // State declarations
  const [templateHtml, setTemplateHtml] = useState("");
  const [deliveryNoteProducts, setDeliveryNoteProducts] = useState([]);
  const blocker = useUnsavedChanges(deliveryNoteProducts.length > 0);
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState("");
  const [deliveryNoteDate, setDeliveryNoteDate] = useState(new Date());
  const [previewHtml, setPreviewHtml] = useState("");
  const [openPreview, setOpenPreview] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [deliveryNoteVAT, setDeliveryNoteVAT] = useState(21);
  const [creatingDeliveryNote, setCreatingDeliveryNote] = useState(false);
  const [isTvaIncluded, setIsTvaIncluded] = useState(true);
  
  // Customer dialog state
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);

  // Product dialog state
  const [openProductDialog, setOpenProductDialog] = useState(false);

  // VAT rates from DB
  const [vatRates, setVatRates] = useState([]);

  // Note templates state
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethodError, setPaymentMethodError] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [useMultiplePayments, setUseMultiplePayments] = useState(false);
  const [invoicePaidAmount, setInvoicePaidAmount] = useState(0);
  const [invoiceDueAmount, setInvoiceDueAmount] = useState("");
  const [paidAmountError, setPaidAmountError] = useState(false);

  // Discount state (disabled for delivery notes)
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);

  // Get payment methods from Redux store
  const paymentMethodOptions = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  const { t, i18n } = useTranslation();
  const language = useSelector((state) => state.settings?.language) || i18n.language || "nl";
  
  const debounceRef = useRef();

  // Fetch payment methods from Redux store
  useEffect(() => {
    if (!paymentMethodOptions || paymentMethodOptions.length === 0) {
      dispatch(fetchPaymentMethods());
    } else {
    }
  }, [dispatch, paymentMethodOptions]);

  // Fetch VAT rates from database
  useEffect(() => {
    const fetchVatRates = async () => {
      try {
        const vatData = await vatService.getVatRates();
        const rates = vatData?.payload || vatData || [];
        setVatRates(rates);
      } catch (error) {
        console.error('❌ Error fetching VAT rates:', error);
      }
    };
    fetchVatRates();
  }, []);

  // Fetch note templates for DeliveryNote
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        // Skip if templates already loaded
        if (noteTemplates.length > 0) return;
        
        const templatesData = await documentNoteTemplateService.getTemplatesByType('DeliveryNote');
        const allTemplates = templatesData?.payload || [];
        // Filter templates by DocumentType.DeliveryNote (3)
        const templates = allTemplates.filter(t => 
          t.DocumentType === 3 || // DocumentType.DeliveryNote
          t.DocumentType === 99   // DocumentType.All
        );
        setNoteTemplates(templates);
        
        // Select first template by default (only for new delivery notes, not edit mode)
        if (templates.length > 0 && !isEditMode && !customText) {
          const defaultTemplate = templates.find(t => t.IsDefault) || templates[0];
          setSelectedTemplate(defaultTemplate.Id);
          const plainText = stripHtml(defaultTemplate.NoteContent);
          if (plainText.length <= MAX_CUSTOM_TEXT_LENGTH) {
            dispatch(setCustomText(defaultTemplate.NoteContent));
          } else {
            const allowedText = plainText.slice(0, MAX_CUSTOM_TEXT_LENGTH);
            dispatch(setCustomText(allowedText));
          }
        }
      } catch (error) {
        console.error('❌ Error fetching note templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  // Sync paid amount with multiple payment methods
  useEffect(() => {
    if (useMultiplePayments && paymentMethods.length > 0) {
      const totalPaid = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
      setInvoicePaidAmount(totalPaid);
    }
  }, [useMultiplePayments, paymentMethods]);

  // Calculate due amount
  useEffect(() => {
    const totalGross = deliveryNoteProducts.reduce((sum, p) => sum + Number(p.TotalIncl), 0);
    const paid = parseFloat(invoicePaidAmount) || 0;
    const price = parseFloat(totalGross) || 0;
    setInvoiceDueAmount((price - paid >= 0 ? price - paid : 0).toFixed(2));
  }, [invoicePaidAmount, deliveryNoteProducts]);

  // Fetch initial data
  useEffect(() => {
    if (isEditMode) {
      return;
    }
    
    const fetchInitialData = async () => {
      setDataLoading(true);
      
      try {
        // Generate delivery note number locally (DDMMYY + 4 random digits)
        setDeliveryNoteNumber(generateDeliveryNoteNumber());

        // Fetch template
        let templateFile = "deliverynote-template-nl.html";
        if (language === "fr") templateFile = "deliverynote-template-fr.html";
        else if (language === "en") templateFile = "deliverynote-template-en.html";
        
        try {
          const response = await fetch(`/deliverynotetemplates/${templateFile}`);
          if (response.ok) {
            const html = await response.text();
            setTemplateHtml(html);
          } else {
            throw new Error(`Failed to load template: ${response.status}`);
          }
        } catch (templateError) {
          console.error('❌ Error fetching template:', templateError);
          setTemplateHtml("");
          toast.warning(t("failed_to_load_template"));
        }
        
      } catch (error) {
        console.error('❌ Critical error during initial data fetch:', error);
        toast.error(t("failed_to_load_initial_data"));
      } finally {
        setDataLoading(false);
      }
    };

    fetchInitialData();
  }, [language, t, dispatch, isEditMode]);

  // Load delivery note for edit
  useEffect(() => {
    if (isEditMode && deliveryNoteId) {
      loadDeliveryNoteForEdit(deliveryNoteId);
    } else {
      dispatch(setSelectedCustomer(null));
      dispatch(setSelectedProduct(null));
      dispatch(setQuantity(1));
      dispatch(setPrice(""));
      dispatch(setCustomText(""));
      setDeliveryNoteProducts([]);
    }
  }, [deliveryNoteId, isEditMode]);

  // Load delivery note data for editing
  const loadDeliveryNoteForEdit = async (id) => {
    try {
      setDataLoading(true);
      const deliveryNoteData = await deliveryNoteService.getDeliveryNoteById(id);
      
      if (deliveryNoteData) {
        setDeliveryNoteNumber(deliveryNoteData.DeliveryNoteNumber || '');
        
        if (deliveryNoteData.DeliveryNoteDate || deliveryNoteData.CreatedDate) {
          setDeliveryNoteDate(new Date(deliveryNoteData.DeliveryNoteDate || deliveryNoteData.CreatedDate));
        }
        
        setDeliveryNoteVAT(Number(deliveryNoteData.VATPercentage || 21));
        dispatch(setCustomText(deliveryNoteData.CustomText || ''));
        
        // Set customer
        if (deliveryNoteData.Customer) {
          const customerData = {
            Id: deliveryNoteData.Customer.Id || deliveryNoteData.CustomerId,
            Name: deliveryNoteData.Customer.Name || deliveryNoteData.CustomerName,
            CompanyName: deliveryNoteData.Customer.CompanyName,
            Email: deliveryNoteData.Customer.Email,
            PhoneNumber: deliveryNoteData.Customer.PhoneNumber,
            VATNumber: deliveryNoteData.Customer.VATNumber,
            Street: deliveryNoteData.Customer.Street,
            City: deliveryNoteData.Customer.City,
            PostCode: deliveryNoteData.Customer.PostCode,
            Country: deliveryNoteData.Customer.Country,
            label: deliveryNoteData.Customer.CompanyName || deliveryNoteData.Customer.Name || deliveryNoteData.CustomerName,
            value: deliveryNoteData.Customer.Id || deliveryNoteData.CustomerId
          };
          dispatch(setSelectedCustomer(customerData));
        }
        
        // Set line items
        if (deliveryNoteData.LineItems && deliveryNoteData.LineItems.length > 0) {
          const mappedProducts = deliveryNoteData.LineItems.map((item, index) => {
            const totalExcl = Number(item.TotalExcl || 0);
            const vatPercentage = item.VAT || deliveryNoteData.VATPercentage || 21;
            
            let totalIncl = item.TotalIncl ? Number(item.TotalIncl) : totalExcl * (1 + vatPercentage / 100);
            const vatAmount = item.VATAmount ? Number(item.VATAmount) : totalIncl - totalExcl;
            
            return {
              Id: item.ProductId,
              Name: item.DisplayName || item.ProductName || item.Name || 'Unknown Product',
              Quantity: Number(item.Quantity || 1),
              Price: String(item.UnitPrice || item.Price || 0),
              VAT: vatPercentage,
              TotalExcl: totalExcl.toFixed(2),
              TotalIncl: totalIncl.toFixed(2),
              VATAmount: vatAmount.toFixed(2),
              TotalVAT: vatAmount.toFixed(2),
              TotalInclVAT: totalIncl.toFixed(2),
              _rowKey: Date.now() + index
            };
          });
          setDeliveryNoteProducts(mappedProducts);
        }
        
        // Fetch template for edit mode
        let templateFile = "deliverynote-nl.html";
        if (language === "fr") templateFile = "deliverynote-fr.html";
        else if (language === "en") templateFile = "deliverynote-en.html";
        
        try {
          const response = await fetch(`/deliverynotetemplates/${templateFile}`);
          if (response.ok) {
            const html = await response.text();
            setTemplateHtml(html);
          } else {
            console.warn(`⚠️ Template not found: ${templateFile}`);
            setTemplateHtml("");
          }
        } catch (templateError) {
          console.error('❌ Error fetching template in edit mode:', templateError);
          setTemplateHtml("");
        }
        
        toast.success(t('delivery_note_loaded_for_editing') || 'Delivery note loaded for editing');
      }
    } catch (error) {
      console.error('❌ Error loading delivery note for edit:', error);
      toast.error(t('failed_to_load_delivery_note') || 'Failed to load delivery note');
      navigate('/delivery-notes');
    } finally {
      setDataLoading(false);
    }
  };

  // Add product to delivery note
  const handleAddProduct = (productToAdd = null, quantityToAdd = null, priceToAdd = null) => {
    const product = productToAdd || selectedProduct;
    const qty = quantityToAdd || quantity;
    const productPrice = priceToAdd || price;
    
    if (product && qty > 0) {
      let netPrice = parseFloat(productPrice);
      let totalExclForProduct;
      let vatAmountForProduct = 0;
      let originalTotalWithVAT = 0;
      
      if (isTvaIncluded && deliveryNoteVAT > 0) {
        originalTotalWithVAT = parseFloat(productPrice) * qty;
        totalExclForProduct = originalTotalWithVAT / (1 + deliveryNoteVAT / 100);
        netPrice = totalExclForProduct / qty;
        
        const roundedTotalExcl = Math.round(totalExclForProduct * 100) / 100;
        vatAmountForProduct = originalTotalWithVAT - roundedTotalExcl;
        totalExclForProduct = roundedTotalExcl;
      } else {
        totalExclForProduct = netPrice * qty;
        originalTotalWithVAT = 0;
      }

      const roundedNetPrice = Math.round(netPrice * 100) / 100;
      const roundedTotalExcl = Math.round(totalExclForProduct * 100) / 100;
      const roundedVATAmount = Math.round(vatAmountForProduct * 100) / 100;

      // Check if product exists
      const existingProductIndex = deliveryNoteProducts.findIndex(
        (p) => p.Id === product.Id && parseFloat(p.Price) === roundedNetPrice
      );

      if (existingProductIndex !== -1) {
        const updatedProducts = [...deliveryNoteProducts];
        const existingProduct = updatedProducts[existingProductIndex];
        const newQuantity = existingProduct.Quantity + qty;
        
        if (isTvaIncluded && deliveryNoteVAT > 0) {
          const newOriginalTotal = parseFloat(productPrice) * newQuantity;
          const newTotalExcl = Math.round((newOriginalTotal / (1 + deliveryNoteVAT / 100)) * 100) / 100;
          const newVATAmount = newOriginalTotal - newTotalExcl;
          
          updatedProducts[existingProductIndex] = {
            ...existingProduct,
            Quantity: newQuantity,
            TotalExcl: newTotalExcl.toFixed(2),
            VATAmount: newVATAmount.toFixed(2),
            TotalIncl: newOriginalTotal.toFixed(2),
            TotalVAT: newVATAmount.toFixed(2),
            TotalInclVAT: newOriginalTotal.toFixed(2),
            OriginalTotal: newOriginalTotal.toFixed(2),
          };
        } else {
          const newTotalExcl = Math.round(newQuantity * roundedNetPrice * 100) / 100;
          const newVATAmount = newTotalExcl * (deliveryNoteVAT / 100);
          const newTotalIncl = newTotalExcl + newVATAmount;
          
          updatedProducts[existingProductIndex] = {
            ...existingProduct,
            Quantity: newQuantity,
            TotalExcl: newTotalExcl.toFixed(2),
            VATAmount: newVATAmount.toFixed(2),
            TotalIncl: newTotalIncl.toFixed(2),
            TotalVAT: newVATAmount.toFixed(2),
            TotalInclVAT: newTotalIncl.toFixed(2),
          };
        }
        setDeliveryNoteProducts(updatedProducts);
      } else {
        let vatAmount, totalIncl;
        if (isTvaIncluded && deliveryNoteVAT > 0) {
          vatAmount = roundedVATAmount;
          totalIncl = originalTotalWithVAT;
        } else {
          vatAmount = roundedTotalExcl * (deliveryNoteVAT / 100);
          totalIncl = roundedTotalExcl + vatAmount;
        }
        
        const newProduct = {
          ...product,
          Quantity: qty,
          Price: roundedNetPrice.toFixed(2),
          TotalExcl: roundedTotalExcl.toFixed(2),
          VATAmount: vatAmount.toFixed(2),
          TotalIncl: totalIncl.toFixed(2),
          TotalVAT: vatAmount.toFixed(2),
          TotalInclVAT: totalIncl.toFixed(2),
          _rowKey: Date.now() + Math.random(),
        };
        
        setDeliveryNoteProducts([...deliveryNoteProducts, newProduct]);
      }
      
      // Only clear Redux state if we're using the default (Redux) values
      if (!productToAdd) {
        dispatch(setSelectedProduct(null));
        dispatch(setQuantity(1));
        dispatch(setPrice(""));
      }
    }
  };

  const handleRemoveProduct = (rowKey) => {
    setDeliveryNoteProducts(deliveryNoteProducts.filter((p) => p._rowKey !== rowKey));
  };

  // Calculate totals (no discount for delivery notes)
  const totalExcl = deliveryNoteProducts.reduce((sum, p) => sum + Number(p.TotalExcl), 0);
  const totalGross = deliveryNoteProducts.reduce((sum, p) => sum + Number(p.TotalIncl), 0);

  const grandTotal = totalGross;
  const vatRate = deliveryNoteVAT / 100;
  const totalExclAfterDiscount = grandTotal / (1 + vatRate);
  const totalVAT = grandTotal - totalExclAfterDiscount;

  // Format date
  function formatDate(date) {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Get filled template for preview
  const getFilledTemplate = () => {
    if (!templateHtml) {
      console.error("❌ Template HTML is empty in getFilledTemplate");
      return "";
    }
    
    const totalQuantity = deliveryNoteProducts.reduce((sum, p) => sum + Number(p.Quantity), 0);
    const cleanCustomText = stripHtml(customText).trim();

    const customTextSection = cleanCustomText
      ? `<div style="margin: 24px 0 16px 0; padding: 12px 16px; background: #fffbe6; border: 1px solid #ffe082; border-radius: 6px;">
        <span style="font-weight: bold; color: #d32f2f; font-size: 1.1em; margin-right: 8px;">${t("notes")}</span>
        <span style="font-weight: 600; color: #795548;">${cleanCustomText}</span>
      </div>`
      : "";
    
    // Notes section - only show if there's content
    const notesSection = cleanCustomText
      ? `<div class="notes-section">
        <h4>Notes</h4>
        <p>${cleanCustomText}</p>
      </div>`
      : "";

    const VATSection = deliveryNoteVAT > 0
      ? `<div><span>${deliveryNoteVAT}% ${t("vat")} on €${totalExcl.toFixed(2)}:</span><span>€${totalVAT.toFixed(2)}</span></div>`
      : `<div><span>0% ${t("vat")}:</span><span>€0.00</span></div>`;

    // No discount for delivery notes
    const DiscountRow = '';

    const ProductRows = deliveryNoteProducts.map((p) => `
      <tr>
        <td>${p.Name}</td>
        <td class="text-center">${p.Quantity}</td>
        <td class="text-right">€${parseFloat(p.Price || 0).toFixed(2)}</td>
        <td class="text-right">€${parseFloat(p.TotalExcl || 0).toFixed(2)}</td>
        <td class="text-right">€${parseFloat(p.VATAmount || 0).toFixed(2)}</td>
        <td class="text-right">€${parseFloat(p.TotalIncl || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    let filledTemplate = templateHtml
      // Company Info
      .replace(/{{CompanyName}}/g, storeInfo?.name || "N/A")
      .replace(/{{CompanyVAT}}/g, storeInfo?.vat || "N/A")
      .replace(/{{CompanyEmail}}/g, storeInfo?.email || "N/A")
      .replace(/{{CompanyPhone}}/g, storeInfo?.phone || "N/A")
      .replace(/{{CompanyAddress}}/g, storeInfo?.address || "")
      .replace(/{{CompanyCity}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' ') || "")
      .replace(/{{CompanyStreetAddress}}/g, storeInfo?.address || "")
      .replace(/{{CompanyCityCountry}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' ') || "")
      .replace(/{{CompanyIBAN}}/g, storeInfo?.iban || "N/A")
      .replace(/{{CompanyBankDetail}}/g, storeInfo?.iban || "N/A")
      .replace(/{{CompanyBankAccount}}/g, storeInfo?.iban || "N/A")
      
      // Customer Info
      .replace(/{{CustomerName}}/g, selectedCustomer?.CompanyName || selectedCustomer?.Name || "N/A")
      .replace(/{{CustomerId}}/g, selectedCustomer?.Id || "")
      .replace(/{{CustomerVAT}}/g, selectedCustomer?.VATNumber || selectedCustomer?.vat || "N/A")
      .replace(/{{CustomerNumber}}/g, selectedCustomer?.Id || "")
      .replace(/{{CustomerEmail}}/g, selectedCustomer?.Email || "N/A")
      .replace(/{{CustomerPhone}}/g, selectedCustomer?.PhoneNumber || "N/A")
      .replace(/{{CustomerAddress}}/g, selectedCustomer?.Street ? `${selectedCustomer?.Street || ""} ${selectedCustomer?.Box || ""}`.trim() : "N/A")
      .replace(/{{CustomerCity}}/g, [selectedCustomer?.PostCode, selectedCustomer?.City, selectedCustomer?.Country].filter(Boolean).join(' ') || "N/A")
      .replace(/{{CustomerStreetAddress}}/g, `${selectedCustomer?.Street || ""} ${selectedCustomer?.Box || ""}`.trim() || "N/A")
      .replace(/{{CustomerCityCountry}}/g, [selectedCustomer?.PostCode, selectedCustomer?.City, selectedCustomer?.Country].filter(Boolean).join(' ') || "N/A")
      
      // Delivery Note Info
      .replace(/{{DeliveryNoteNumber}}/g, deliveryNoteNumber)
      .replace(/{{DeliveryNoteDate}}/g, formatDate(deliveryNoteDate))
      .replace(/{{Status}}/g, "")
      .replace(/status-{{Status}}/g, "")
      
      // Product Info (for backwards compatibility)
      .replace(/{{ProductName}}/g, selectedProduct?.Name || "")
      .replace(/{{ProductId}}/g, selectedProduct?.Id || "")
      .replace(/{{Quantity}}/g, quantity.toString())
      .replace(/{{Price}}/g, price.toString())
      .replace(/{{VAT}}/g, deliveryNoteVAT.toString())
      
      // Product Rows and Line Items
      .replace(/{{LineItems}}/g, ProductRows)
      .replace(/{{{ProductRows}}}/g, ProductRows)
      .replace(/{{TotalQuantity}}/g, totalQuantity.toString())
      .replace(/{{{DiscountRow}}}/g, DiscountRow)
      
      // Financial Totals
      .replace(/{{Subtotal}}/g, `€${totalGross.toFixed(2)}`)
      .replace(/{{TotalExcl}}/g, totalExcl.toFixed(2))
      .replace(/{{VATPercentage}}/g, deliveryNoteVAT.toString())
      .replace(/{{TotalVAT}}/g, totalVAT.toFixed(2))
      .replace(/{{VATAmount}}/g, `€${totalVAT.toFixed(2)}`)
      .replace(/{{Discount}}/g, "€0.00")
      .replace(/{{TotalAmount}}/g, grandTotal.toFixed(2))
      .replace(/{{Total}}/g, `€${grandTotal.toFixed(2)}`)
      .replace(/{{GrandTotal}}/g, grandTotal.toFixed(2))
      
      // Payment Information
      .replace(/{{InvoicePaidAmount}}/g, parseFloat(invoicePaidAmount || 0).toFixed(2))
      .replace(/{{InvoiceDueAmount}}/g, parseFloat(invoiceDueAmount || 0).toFixed(2))
      .replace(/{{{PaymentMethodsSection}}}/g, (() => {
        const paidAmount = parseFloat(invoicePaidAmount || 0);
        if (paidAmount === 0) return '';
        
        if (useMultiplePayments && paymentMethods && paymentMethods.length > 0) {
          return paymentMethods
            .map(pm => `<div class="total-row"><span>${pm.name || 'Payment'}:</span><strong>€${parseFloat(pm.amount || 0).toFixed(2)}</strong></div>`)
            .join('');
        } else if (paymentMethod && paymentMethod !== '') {
          const pmLabel = paymentMethodOptions.find(opt => opt.value === paymentMethod)?.label || paymentMethod;
          return `<div class="total-row"><span>Payment Method:</span><strong>${pmLabel}</strong></div>`;
        }
        return '';
      })())
      
      // Page Numbering
      .replace(/{{PageNumber}}/g, '1')
      .replace(/{{TotalPages}}/g, '1')
      
      // VAT Section
      .replace(/{{VATSection}}/g, VATSection)
      
      // Custom Text and Notes
      .replace(/{{CustomText}}/g, cleanCustomText)
      .replace(/{{CustomTextSection}}/g, customTextSection)
      .replace(/<div class="notes-section">\s*<h4>Notes<\/h4>\s*<p>{{Notes}}<\/p>\s*<\/div>/g, notesSection)
      .replace(/{{Notes}}/g, cleanCustomText);

    return filledTemplate;
  };

  // Preview delivery note
  const handlePreview = () => {
    if (!selectedCustomer) {
      toast.error(t("please_select_customer") || "Please select a customer");
      return;
    }
    
    if (deliveryNoteProducts.length === 0) {
      toast.error(t("please_add_products") || "Please add at least one product");
      return;
    }
    
    if (!templateHtml) {
      toast.error(t("failed_to_load_template") || "Template not loaded. Please refresh the page.");
      console.error("❌ Template HTML is empty");
      return;
    }
    
    try {
      const filled = getFilledTemplate();
      if (!filled) {
        toast.error("Failed to generate preview. Please try again.");
        console.error("❌ getFilledTemplate returned empty result");
        return;
      }
      setPreviewHtml(filled);
      setOpenPreview(true);
    } catch (error) {
      console.error("❌ Error generating preview:", error);
      toast.error("Failed to generate preview. Please try again.");
    }
  };

  // Generate PDF
  const handleGeneratePDF = async () => {
    try {
      if (!storeInfo) {
        toast.warning('Loading store information...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (!storeInfo) {
          toast.error('Store information not available. Please refresh the page.');
          return;
        }
      }

      const rawHtml = getFilledTemplate();
      // Inject a style to force-disable box-shadow and ensure full-width rendering for PDF
      const filledHtml = rawHtml.replace(
        '</style>',
        `  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .delivery-note-container { box-shadow: none !important; margin: 0 !important; width: 794px !important; max-width: 794px !important; }
  </style>`
      );
      const opt = {
        margin: 0,
        filename: `delivery-note-${deliveryNoteNumber}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          logging: false,
          windowWidth: 794,
          letterRendering: true
        },
        jsPDF: { 
          unit: "mm", 
          format: "a4", 
          orientation: "portrait" 
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: ['tr', 'td', 'img', '.totals-section', '.notes-section']
        }
      };

      await html2pdf().set(opt).from(filledHtml).save();
      
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      toast.error(t("failed_to_generate_pdf") || "Failed to generate PDF");
    }
  };

  // Save delivery note
  const handleSaveDeliveryNote = async () => {
    // Validation
    if (!selectedCustomer) {
      toast.error(t("please_select_customer") || "Please select a customer");
      return;
    }
    
    if (deliveryNoteProducts.length === 0) {
      toast.error(t("please_add_products") || "Please add at least one product");
      return;
    }

    setCreatingDeliveryNote(true);

    try {
      const deliveryNoteData = {
        ...(isEditMode && deliveryNoteId ? { Id: parseInt(deliveryNoteId) } : {}),
        DeliveryNoteNumber: deliveryNoteNumber,
        DeliveryNoteDate: deliveryNoteDate.toISOString(),
        CustomerId: selectedCustomer?.Id || selectedCustomer?.value,
        CustomText: stripHtml(customText),
        VATPercentage: deliveryNoteVAT,
        TotalAmount: grandTotal,
        SubTotal: parseFloat(totalExclAfterDiscount.toFixed(2)),
        TotalVATAmount: parseFloat(totalVAT.toFixed(2)),
        Discount: 0,
        DiscountType: 'none',
        DiscountValue: 0,
        LineItems: deliveryNoteProducts.map((p) => ({
          ProductId: p.Id,
          Quantity: p.Quantity,
          Price: p.Price,
          TotalExcl: p.TotalExcl,
          VatAmount: p.VATAmount,
          TotalIncl: p.TotalIncl,
          TotalVAT: p.TotalVAT || p.VATAmount,
          TotalInclVAT: p.TotalInclVAT || p.TotalIncl,
        })),
      };

      let response;
      if (isEditMode && deliveryNoteId) {
        response = await deliveryNoteService.updateDeliveryNote(deliveryNoteId, deliveryNoteData);
      } else {
        response = await deliveryNoteService.addDeliveryNote(deliveryNoteData);
      }

      if (response) {
        toast.success(isEditMode ? t("delivery_note_updated_success") || "Delivery note updated successfully" : t("delivery_note_created_success") || "Delivery note created successfully");
        await handleGeneratePDF();
        
        if (!isEditMode) {
          resetDeliveryNoteForm();
        }
        
        setTimeout(() => {
          navigate('/sales/delivery-notes');
        }, 1500);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error(isEditMode ? '❌ Error updating delivery note:' : '❌ Error creating delivery note:', error);
      
      if (error.response?.status === 400) {
        toast.error(t("invalid_delivery_note_data") || "Invalid delivery note data");
      } else if (error.response?.status === 401) {
        toast.error(t("session_expired") || "Session expired");
      } else if (error.code === 'ERR_NETWORK') {
        toast.error(t("network_error") || "Network error");
      } else {
        toast.error(error.response?.data?.message || (isEditMode ? t("delivery_note_updated_error") : t("delivery_note_created_error")) || "An error occurred");
      }
    } finally {
      setCreatingDeliveryNote(false);
    }
  };

  // Reset form
  const resetDeliveryNoteForm = async () => {
    dispatch(setSelectedCustomer(null));
    dispatch(setSelectedProduct(null));
    dispatch(setQuantity(1));
    dispatch(setPrice(""));
    dispatch(setCustomText(""));
    setDeliveryNoteProducts([]);
    setDeliveryNoteVAT(21);
    setDeliveryNoteDate(new Date());
    setIsTvaIncluded(true);
    
    setDeliveryNoteNumber(generateDeliveryNoteNumber());
  };

  // Load customer options
  const loadCustomerOptions = async (inputValue) => {
    try {
      const data = await searchCustomers(inputValue);
      
      const customersArray = Array.isArray(data?.payload)
        ? data.payload
        : Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];

      return customersArray.map((c) => ({
        ...c,
        value: c.Id,
        label: `${c.CompanyName || c.Name} (${c.Email || 'No email'})`,
      }));
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      
      if (error.response?.status === 401) {
        toast.error(t("session_expired"));
      } else if (error.code === 'ERR_NETWORK') {
        toast.error(t("network_error"));
      }
      
      return [];
    }
  };

  // Load product options
  const loadProductOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue) return callback([]);

      try {
        const barcodeLengths = [8, 12, 13, 14];
        let paramsObj;

        if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (/^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue, id: inputValue };
        } else if (inputValue.length >= 3) {
          paramsObj = { name: inputValue };
        } else {
          return callback([]);
        }

        const data = await searchProducts(paramsObj);

        const productsArray = Array.isArray(data?.payload)
          ? data.payload
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        const limitedProducts = productsArray.slice(0, 50);

        callback(
          limitedProducts.map((p) => ({
            value: p.Id,
            label: `${p.Name} (€${p.SalePrice || 0})${p.SKU ? " - SKU: " + p.SKU : ""}${p.BarCode ? " - BarCode: " + p.BarCode : ""}`,
            ...p,
          }))
        );
      } catch (error) {
        console.error('❌ Error searching products:', error);
        
        if (error.response?.status === 401) {
          toast.error(t("session_expired"));
        } else if (error.code === 'ERR_NETWORK') {
          toast.error(t("network_error"));
        }
        
        callback([]);
      }
    }, 350);
  };

  // Recalculate when VAT changes
  useEffect(() => {
    if (deliveryNoteProducts.length > 0) {
      const updatedProducts = deliveryNoteProducts.map(product => {
        const totalIncl = Number(product.TotalIncl);
        const newTotalExcl = totalIncl / (1 + deliveryNoteVAT / 100);
        const newVATAmount = totalIncl - newTotalExcl;
        const newPrice = newTotalExcl / Number(product.Quantity);
        
        return {
          ...product,
          Price: newPrice.toFixed(2),
          VAT: deliveryNoteVAT,
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2)
        };
      });
      setDeliveryNoteProducts(updatedProducts);
    }
  }, [deliveryNoteVAT]);

  // Customer dialog handlers
  const handleOpenCustomerDialog = () => {
    setOpenCustomerDialog(true);
  };

  const handleCloseCustomerDialog = () => {
    setOpenCustomerDialog(false);
  };

  const handleCustomerCreated = (createdCustomer) => {
    dispatch(setSelectedCustomer(createdCustomer));
    toast.success(t("customer_created_and_selected") || "Customer created and selected!");
    setOpenCustomerDialog(false);
  };

  // Handler for full payment
  const handleFullPayment = () => {
    setInvoicePaidAmount(grandTotal.toFixed(2));
    setPaidAmountError(false);
  };

  // Handler for template selection
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = noteTemplates.find(t => t.Id === templateId);
    if (template) {
      const plainText = stripHtml(template.NoteContent);
      if (plainText.length <= MAX_CUSTOM_TEXT_LENGTH) {
        dispatch(setCustomText(template.NoteContent));
      } else {
        const allowedText = plainText.slice(0, MAX_CUSTOM_TEXT_LENGTH);
        dispatch(setCustomText(allowedText));
        toast.warning(t("template_content_truncated"));
      }
    }
  };

  // Show loading or error state
  if (!templateHtml && !dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper 
          elevation={3}
          sx={{ 
            p: 4,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <LocalShippingIcon sx={{ fontSize: 60, color: 'error.main' }} />
            <Typography variant="h6" color="error" textAlign="center">
              {t("failed_to_load_template") || "Failed to load template"}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t("please_refresh_or_contact_support") || "Please refresh or contact support"}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              startIcon={<RefreshIcon />}
            >
              {t("refresh_page") || "Refresh Page"}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{
      background: '#f5f5f5',
      minHeight: '100vh',
      py: 3
    }}>
      <Container 
        maxWidth={false}
        sx={{ 
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1600px',
          margin: '0 auto'
        }}
      >
        {/* Back Button */}
        {isEditMode && deliveryNoteId && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/delivery-notes/view/${deliveryNoteId}`)}
            sx={{
              mb: 2,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#764ba2',
                bgcolor: alpha('#667eea', 0.05)
              }
            }}
          >
            {t("back_to_view") || "Back to View"}
          </Button>
        )}

        {/* Delivery Note Container - Matches Template A4 Style */}
        <Paper
          elevation={3}
          sx={{
            background: 'white',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'visible',
            maxWidth: '100%'
          }}
        >
          <Box sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 } }}>
            {/* Delivery Note Number Badge - Top Right */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Box sx={{
                p: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px'
              }}>
                <Typography variant="overline" fontWeight="600" sx={{ fontSize: '0.75rem', letterSpacing: '1px', display: 'block' }}>
                  {t("delivery_note") || "DELIVERY NOTE"}
                </Typography>
                <Typography variant="h6" fontWeight="700" sx={{ letterSpacing: '0.5px' }}>
                  {deliveryNoteNumber}
                </Typography>
              </Box>
            </Box>

            {/* TOP SECTION: Company Info (Left) + Customer (Right) - Side by Side */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
              {/* LEFT: Company Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="overline" fontWeight="600" color="#667eea" sx={{ fontSize: '0.75rem', letterSpacing: '1px', mb: 1, display: 'block' }}>
                  {t("from") || "FROM"}
                </Typography>
                <Typography variant="h4" fontWeight="700" color="#2c3e50" sx={{ mb: 1.5 }}>
                  {storeInfo?.name || t("company_name") || "Your Company Name"}
                </Typography>
                <Typography variant="body2" color="#555" sx={{ lineHeight: 2 }}>
                  {storeInfo?.vat && <><strong>{t("vat_number")}:</strong> {storeInfo.vat}<br /></>}
                  {storeInfo?.address && <>{storeInfo.address}<br /></>}
                  {storeInfo?.postCode && storeInfo?.city && <>{storeInfo.postCode} {storeInfo.city}{storeInfo?.country ? ', ' + storeInfo.country : ''}</>}
                </Typography>
              </Grid>

              {/* RIGHT: Customer Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="overline" fontWeight="600" color="#667eea" sx={{ fontSize: '0.75rem', letterSpacing: '1px', mb: 1, display: 'block' }}>
                  {t("ship_to") || "SHIP TO"}
                </Typography>
                
                {!selectedCustomer ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <CustomerSelect
                        value={selectedCustomer}
                        onChange={(selectedOption) => {
                          dispatch(setSelectedCustomer(selectedOption));
                        }}
                        loadOptions={loadCustomerOptions}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleOpenCustomerDialog}
                      sx={{
                        borderRadius: '6px',
                        px: 2,
                        height: '38px',
                        whiteSpace: 'nowrap',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: '#667eea',
                        color: '#667eea',
                        '&:hover': {
                          borderColor: '#764ba2',
                          bgcolor: alpha('#667eea', 0.05)
                        }
                      }}
                    >
                      {t("add_new")}
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h4" fontWeight="700" color="#2c3e50">
                        {selectedCustomer.Name || selectedCustomer.CompanyName}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          dispatch(setSelectedCustomer(null));
                        }}
                        sx={{ 
                          color: '#667eea',
                          '&:hover': {
                            bgcolor: alpha('#667eea', 0.08)
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="#555" sx={{ lineHeight: 2 }}>
                      {(selectedCustomer.VATNumber || selectedCustomer.vat) && 
                        <><strong>{t("vat_number")}:</strong> {selectedCustomer.VATNumber || selectedCustomer.vat}<br /></>}
                      {selectedCustomer.Street && <>{selectedCustomer.Street}<br /></>}
                      {selectedCustomer.City && <>{selectedCustomer.PostCode || ''} {selectedCustomer.City}</>}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* DELIVERY NOTE DETAILS ROW: Date, VAT Rate, Items Count, Price Type */}
            <Box sx={{
              display: 'flex',
              gap: 3,
              mb: 4,
              p: 3,
              background: '#f8f9fa',
              borderRadius: '8px',
              borderLeft: '4px solid #667eea',
              flexWrap: 'wrap',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <Typography variant="caption" fontWeight="600" color="#2c3e50" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
                  {t("delivery_note_date")}
                </Typography>
                <TextField
                  type="date"
                  value={deliveryNoteDate ? new Date(deliveryNoteDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => setDeliveryNoteDate(new Date(e.target.value))}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'white',
                      '& fieldset': { borderColor: '#e0e0e0' }
                    }
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '120px' }}>
                <Typography variant="caption" fontWeight="600" color="#2c3e50" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
                  {t("vat_rate")} (%)
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={deliveryNoteVAT}
                    onChange={(e) => setDeliveryNoteVAT(parseFloat(e.target.value))}
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' }
                    }}
                  >
                    {vatRates.length > 0 ? (
                      vatRates.map((rate) => (
                        <MenuItem key={rate.Id} value={rate.VATPercentage}>
                          {rate.VATPercentage}%
                        </MenuItem>
                      ))
                    ) : (
                      <>
                        <MenuItem value={0}>0%</MenuItem>
                        <MenuItem value={6}>6%</MenuItem>
                        <MenuItem value={12}>12%</MenuItem>
                        <MenuItem value={21}>21%</MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1, minWidth: '120px' }}>
                <Typography variant="caption" fontWeight="600" color="#2c3e50" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
                  {t("items")}
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#667eea" sx={{ mt: 0.5 }}>
                  {deliveryNoteProducts.length}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: '140px' }}>
                <Typography variant="caption" fontWeight="600" color="#2c3e50" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
                  {t("price_type") || "Price Type"}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isTvaIncluded}
                      onChange={(e) => setIsTvaIncluded(e.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#667eea',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#667eea',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                      {t("tva_included") || "TVA Incl"}
                    </Typography>
                  }
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>

            {/* PRODUCTS SECTION: Table with Header Always Visible */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5 }}>
                        {t("product_name") || "PRODUCT NAME"}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, textAlign: 'center', width: '120px' }}>
                        {t("quantity") || "QUANTITY"}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, textAlign: 'right', width: '120px' }}>
                        {t("price") || "PRICE"}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, textAlign: 'right', width: '100px' }}>
                        {t("vat") || "VAT"}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, textAlign: 'right', width: '130px' }}>
                        {t("total_excl") || "TOTAL EXCL."}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, textAlign: 'right', width: '130px' }}>
                        {t("total_incl") || "TOTAL INCL."}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, textAlign: 'center', width: '100px' }}>
                        {t("actions") || "ACTIONS"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Added Products Rows */}
                    {deliveryNoteProducts.map((p) => {
                      const vatPerUnit = (parseFloat(p.VATAmount) / p.Quantity).toFixed(2);
                      const totalExcl = parseFloat(p.TotalExcl || 0);
                      const totalIncl = parseFloat(p.TotalIncl || 0);
                      
                      return (
                        <TableRow 
                          key={p._rowKey}
                          sx={{
                            '&:hover': { bgcolor: '#f8f9fa' },
                            borderBottom: '1px solid #e0e0e0'
                          }}
                        >
                          <TableCell sx={{ py: 1.5, fontSize: '0.85rem', color: '#333' }}>
                            <Typography variant="body2" fontWeight={500}>
                              {p.Name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                            <TextField
                              type="number"
                              value={p.Quantity}
                              onChange={(e) => {
                                const newQuantity = parseFloat(e.target.value) || 0;
                                if (newQuantity <= 0) return;
                                const updatedProducts = deliveryNoteProducts.map((prod) => {
                                  if (prod._rowKey === p._rowKey) {
                                    const pricePerUnit = parseFloat(prod.Price);
                                    const newTotalExcl = pricePerUnit * newQuantity;
                                    const vatRate = deliveryNoteVAT / 100;
                                    const newVATAmount = newTotalExcl * vatRate;
                                    const newTotalIncl = newTotalExcl + newVATAmount;
                                    return {
                                      ...prod,
                                      Quantity: newQuantity,
                                      TotalExcl: newTotalExcl.toFixed(2),
                                      VATAmount: newVATAmount.toFixed(2),
                                      TotalIncl: newTotalIncl.toFixed(2),
                                      TotalVAT: newVATAmount.toFixed(2),
                                      TotalInclVAT: newTotalIncl.toFixed(2),
                                    };
                                  }
                                  return prod;
                                });
                                setDeliveryNoteProducts(updatedProducts);
                              }}
                              size="small"
                              inputProps={{ min: 1, step: 1 }}
                              sx={{
                                width: '80px',
                                '& .MuiOutlinedInput-root': {
                                  '& input': { textAlign: 'center', py: 0.5 }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                            <TextField
                              type="number"
                              value={p.Price}
                              onChange={(e) => {
                                const newPrice = parseFloat(e.target.value) || 0;
                                if (newPrice < 0) return;
                                const updatedProducts = deliveryNoteProducts.map((prod) => {
                                  if (prod._rowKey === p._rowKey) {
                                    const newTotalExcl = newPrice * prod.Quantity;
                                    const vatRate = deliveryNoteVAT / 100;
                                    const newVATAmount = newTotalExcl * vatRate;
                                    const newTotalIncl = newTotalExcl + newVATAmount;
                                    return {
                                      ...prod,
                                      Price: newPrice.toFixed(2),
                                      TotalExcl: newTotalExcl.toFixed(2),
                                      VATAmount: newVATAmount.toFixed(2),
                                      TotalIncl: newTotalIncl.toFixed(2),
                                      TotalVAT: newVATAmount.toFixed(2),
                                      TotalInclVAT: newTotalIncl.toFixed(2),
                                    };
                                  }
                                  return prod;
                                });
                                setDeliveryNoteProducts(updatedProducts);
                              }}
                              size="small"
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{
                                width: '100px',
                                '& .MuiOutlinedInput-root': {
                                  '& input': { textAlign: 'right', py: 0.5, fontWeight: 500 }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 500, color: '#f57c00' }}>
                            €{vatPerUnit}
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 600, color: '#388e3c' }}>
                            €{p.TotalExcl}
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                            <TextField
                              type="number"
                              value={p.TotalIncl}
                              onChange={(e) => {
                                const newTotalIncl = parseFloat(e.target.value) || 0;
                                const updatedProducts = deliveryNoteProducts.map((prod) => {
                                  if (prod._rowKey === p._rowKey) {
                                    const vatRate = deliveryNoteVAT / 100;
                                    const newTotalExcl = newTotalIncl / (1 + vatRate);
                                    const newVATAmount = newTotalIncl - newTotalExcl;
                                    const newPrice = (newTotalExcl / prod.Quantity).toFixed(2);
                                    return {
                                      ...prod,
                                      TotalIncl: newTotalIncl.toFixed(2),
                                      TotalExcl: newTotalExcl.toFixed(2),
                                      VATAmount: newVATAmount.toFixed(2),
                                      TotalVAT: newVATAmount.toFixed(2),
                                      TotalInclVAT: newTotalIncl.toFixed(2),
                                      Price: newPrice,
                                    };
                                  }
                                  return prod;
                                });
                                setDeliveryNoteProducts(updatedProducts);
                              }}
                              size="small"
                              sx={{
                                width: '90px',
                                '& .MuiOutlinedInput-root': {
                                  '& input': { textAlign: 'right', py: 0.5, fontWeight: 600 }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveProduct(p._rowKey)}
                              sx={{
                                color: '#d32f2f',
                                '&:hover': { bgcolor: alpha('#d32f2f', 0.1) }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Product Search Row - For adding new products */}
                    <TableRow sx={{ bgcolor: '#fffef0', borderTop: '2px solid #667eea' }}>
                      <TableCell colSpan={7} sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => setOpenProductDialog(true)}
                            sx={{
                              color: '#667eea',
                              bgcolor: alpha('#667eea', 0.1),
                              '&:hover': { bgcolor: alpha('#667eea', 0.2) }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <Box sx={{ flex: 1 }}>
                            <ProductSelect
                              value={null}
                              onChange={(selectedOption) => {
                                if (selectedOption) {
                                  const productPrice = selectedOption.SalePrice || selectedOption.Price || selectedOption.price || "";
                                  
                                  // Immediately add the product with the selected data
                                  handleAddProduct(selectedOption, 1, productPrice);
                                }
                              }}
                              loadOptions={loadProductOptions}
                              placeholder={t("search_products") || "Search and add products..."}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Totals Row - At the bottom */}
                    {deliveryNoteProducts.length > 0 && (
                      <TableRow sx={{ bgcolor: '#f0f0f0', borderTop: '2px solid #667eea' }}>
                        <TableCell sx={{ py: 1.5, fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                          {t("total") || "TOTAL"}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                          {deliveryNoteProducts.reduce((sum, p) => sum + parseFloat(p.Quantity || 0), 0)}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}></TableCell>
                        <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#f57c00' }}>
                          €{deliveryNoteProducts.reduce((sum, p) => sum + parseFloat(p.VATAmount || 0), 0).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color:  '#388e3c' }}>
                          €{deliveryNoteProducts.reduce((sum, p) => sum + parseFloat(p.TotalExcl || 0), 0).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.95rem', color: '#667eea' }}>
                          €{deliveryNoteProducts.reduce((sum, p) => sum + parseFloat(p.TotalIncl || 0), 0).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {/* BOTTOM SECTION: Payment, Discount, Notes, Totals */}
            <Grid container spacing={3}>
              {/* LEFT: Payment Method, Discount, Custom Text */}
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  {/* Payment Method */}
                  <Box sx={{
                    p: 3,
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #667eea',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <PaymentIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("payment_method")}
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useMultiplePayments}
                          onChange={(e) => {
                            setUseMultiplePayments(e.target.checked);
                            if (e.target.checked) {
                              setPaymentMethod("");
                              setPaymentMethods([]);
                            }
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption" fontWeight={600}>
                          {t("use_multiple_payment_methods")}
                        </Typography>
                      }
                      sx={{ mb: 2 }}
                    />

                    {!useMultiplePayments ? (
                      <FormControl fullWidth size="small" error={paymentMethodError}>
                        <Select
                          value={paymentMethod}
                          onChange={(e) => {
                            setPaymentMethod(e.target.value);
                            setPaymentMethodError(false);
                          }}
                          displayEmpty
                          sx={{ bgcolor: 'white' }}
                        >
                          <MenuItem value="" disabled>
                            <em>{t("select_payment_method") || "Select Payment Method"}</em>
                          </MenuItem>
                          {paymentMethodOptions.map((pm) => (
                            <MenuItem key={pm.id} value={pm.id}>
                              {pm.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {paymentMethodError && (
                          <FormHelperText>{t("please_select_payment_method")}</FormHelperText>
                        )}
                      </FormControl>
                    ) : (
                      <MultiplePaymentMethodsSection
                        paymentMethods={paymentMethods}
                        onPaymentMethodsChange={setPaymentMethods}
                        paymentMethodOptions={paymentMethodOptions.map(pm => ({
                          value: pm.id,
                          label: pm.name
                        }))}
                        totalAmount={grandTotal}
                      />
                    )}
                  </Box>

                  {/* Discount (Disabled for Delivery Notes) */}
                  <Box sx={{
                    p: 3,
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #cccccc',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    opacity: 0.6
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <DiscountIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("discount")} ({t("not_available") || "Not Available"})
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small" disabled>
                          <Select
                            value={discountType}
                            sx={{ bgcolor: 'white' }}
                          >
                            <MenuItem value="percentage">{t("percentage")}</MenuItem>
                            <MenuItem value="fixed">{t("fixed_amount")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          type="number"
                          value={discountValue}
                          size="small"
                          fullWidth
                          disabled
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ bgcolor: 'white' }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Custom Text/Notes */}
                  <Box sx={{
                    p: 3,
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #667eea',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <NotesIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("notes")}
                    </Typography>
                    
                    {/* Template Selector */}
                    {noteTemplates.length > 0 && (
                      <FormControl fullWidth sx={{ mb: 2, bgcolor: 'white' }}>
                        <InputLabel>{t("select_template")}</InputLabel>
                        <Select
                          value={selectedTemplate}
                          onChange={(e) => handleTemplateSelect(e.target.value)}
                          label={t("select_template")}
                        >
                          <MenuItem value="">
                            <em>{t("none")}</em>
                          </MenuItem>
                          {noteTemplates.map((template) => (
                            <MenuItem key={template.Id} value={template.Id}>
                              {template.TemplateName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    
                    <TextField
                      multiline
                      rows={4}
                      value={customText}
                      onChange={(e) => {
                        const newText = e.target.value;
                        if (stripHtml(newText).length <= MAX_CUSTOM_TEXT_LENGTH) {
                          dispatch(setCustomText(newText));
                        }
                      }}
                      placeholder={t("add_custom_notes") || "Add custom notes..."}
                      fullWidth
                      sx={{
                        bgcolor: 'white',
                        borderRadius: '4px'
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={stripHtml(customText).length > MAX_CUSTOM_TEXT_LENGTH ? 'error' : 'text.secondary'}
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {stripHtml(customText).length} / {MAX_CUSTOM_TEXT_LENGTH}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* RIGHT: Totals Summary & Action Buttons */}
              <Grid item xs={12} md={5}>
                <Stack spacing={3}>
                  {deliveryNoteProducts.length === 0 && (
                    <Box sx={{
                      p: 2,
                      borderRadius: '10px',
                      bgcolor: alpha(theme.palette.error.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography
                        variant="body1"
                        color="error"
                        textAlign="center"
                        fontWeight="medium"
                      >
                        {t("please_add_at_least_one_product")}
                      </Typography>
                    </Box>
                  )}

                  {/* Totals Summary Box */}
                  <Box sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    alignSelf: 'flex-end',
                    width: '100%'
                  }}>
                    <Box sx={{ p: 2, background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle1" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                        <CalculateIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                        {t("summary")}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="#555">{t("subtotal")}</Typography>
                          <Typography variant="body1" fontWeight={600}>€{totalExcl.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#e8f4f8', borderRadius: '4px' }}>
                          <Typography variant="body2" color="#0066cc" fontWeight={600}>{t("vat")} ({deliveryNoteVAT}%)</Typography>
                          <Typography variant="body1" fontWeight={600} color="#0066cc">€{totalVAT.toFixed(2)}</Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '6px',
                          mt: 1
                        }}>
                          <Typography variant="h6" fontWeight="700" color="white">{t("total")}</Typography>
                          <Typography variant="h5" fontWeight="700" color="white">€{grandTotal.toFixed(2)}</Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={handleFullPayment}
                          sx={{
                            mt: 2,
                            borderColor: '#667eea',
                            color: '#667eea',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              borderColor: '#764ba2',
                              bgcolor: alpha('#667eea', 0.05)
                            }
                          }}
                        >
                          {t("mark_as_fully_paid")}
                        </Button>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                          <Typography variant="body2" color="#555">{t("paid_amount")}</Typography>
                          <TextField
                            type="number"
                            value={invoicePaidAmount}
                            onChange={(e) => {
                              setInvoicePaidAmount(e.target.value);
                              setPaidAmountError(false);
                            }}
                            size="small"
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: '120px' }}
                            error={paidAmountError}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight={600} color="#d32f2f">{t("due_amount")}</Typography>
                          <Typography variant="h6" fontWeight={700} color="#d32f2f">€{invoiceDueAmount}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>

                  {/* Primary Action Buttons */}
                  <Stack spacing={2}>
                    {/* Preview Delivery Note button */}
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreview}
                      disabled={!selectedCustomer || deliveryNoteProducts.length === 0}
                      sx={{
                        py: 1.5,
                        borderRadius: '12px',
                        borderWidth: '2px',
                        borderColor: '#667eea',
                        color: '#667eea',
                        fontSize: '1rem',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        '&:hover': {
                          borderWidth: '2px',
                          borderColor: '#764ba2',
                          backgroundColor: alpha('#667eea', 0.05)
                        }
                      }}
                    >
                      {t("preview_delivery_note")}
                    </Button>

                    {/* Save/Update Delivery Note button */}
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={creatingDeliveryNote ? null : <SaveIcon />}
                      onClick={handleSaveDeliveryNote}
                      disabled={!selectedCustomer || deliveryNoteProducts.length === 0 || creatingDeliveryNote}
                      sx={{
                        py: 1.5,
                        borderRadius: '12px',
                        background: "linear-gradient(45deg, #667eea 20%, #764ba2 90%)",
                        boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                        fontSize: '1rem',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: creatingDeliveryNote ? 'none' : 'translateY(-2px)',
                          boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)'
                        },
                        '&:disabled': {
                          background: 'linear-gradient(45deg, #9ca3af 20%, #6b7280 90%)',
                        }
                      }}
                    >
                      {creatingDeliveryNote ? (isEditMode ? t("updating_delivery_note") : t("creating_delivery_note")) : (isEditMode ? t("update_delivery_note") : t("create_delivery_note"))}
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Preview Dialog */}
        <InvoicePreviewDialog
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          html={previewHtml}
        />

        {/* Add Customer Dialog - Reusable Component */}
        <AddCustomerDialog
          open={openCustomerDialog}
          onClose={handleCloseCustomerDialog}
          onCustomerCreated={handleCustomerCreated}
          optionalFields={{ vat: true, email: true, phone: true }}
          hidePeppol={true}
        />

        {/* Add Product Dialog */}
        <AddProductDialog
          open={openProductDialog}
          onClose={() => setOpenProductDialog(false)}
          onProductCreated={(createdProduct) => {
            if (createdProduct && createdProduct.Id) {
              const productPrice = createdProduct.Price || createdProduct.SalePrice || createdProduct.price || 0;
              const productForSelection = {
                value: createdProduct.Id,
                label: `${createdProduct.Name} (\u20ac${productPrice})`,
                Id: createdProduct.Id,
                Name: createdProduct.Name,
                SKU: createdProduct.SKU,
                SalePrice: productPrice,
                Price: productPrice,
              };
              dispatch(setSelectedProduct(productForSelection));
              dispatch(setPrice(productPrice.toString()));
              toast.success(t("product_created_and_selected") || "Product created and selected!");
            }
            setOpenProductDialog(false);
          }}
        />
      </Container>

      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onDiscard={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </Box>
  );
}

export default AddDeliveryNotePage;
